// ─────────────────────────────────────────────────────────────────────────────
// App — top-level component.
//
// Single responsibility: extract the checkout token from the URL, validate
// its shape, and render either the live <Checkout /> or an <InvalidLink />
// terminal state.
//
// URL shape: https://checkout.posteering.com/<token>
//            where <token> is 43 chars of base64url alphabet.
//
// Why path-segment, not query-string?
//   Query strings leak into referer headers when the page makes outbound
//   requests (e.g. to Fontshare for fonts). Path segments don't. Keeping
//   the token in the path alone minimizes its exposure surface.
//
// Client-side validation happens BEFORE any network call. A malformed
// token goes straight to <InvalidLink /> without consuming a rate-limit
// slot or triggering a BFF round-trip. Only well-formed tokens ever
// reach the API.
// ─────────────────────────────────────────────────────────────────────────────

import { Checkout } from './Checkout';
import { InvalidLink } from './components/InvalidLink';
import { TOKEN_REGEX } from './api';

export default function App(): JSX.Element {
  // Path is either:
  //   "/"             → no token, render InvalidLink
  //   "/<token>"      → validate and branch
  //   "/<anything>/<else>" → treat as malformed (takes first segment only)
  //
  // window.location.pathname is safe to read synchronously during render;
  // it doesn't change without a full page reload (we have no router).
  const raw = window.location.pathname.replace(/^\/+/, '').split('/')[0] ?? '';

  if (!raw || !TOKEN_REGEX.test(raw)) {
    return <InvalidLink />;
  }

  return <Checkout token={raw} />;
}
